import { IdAsUUID } from "./commonTypes";
import { TypedSelectOption } from "./selectOptions";

export const TABLENAMES = [
  "audit_trails",
  "addresses",
  "contacts",
  "customers",
  "employees",
  "person_base",
  "company_base",
  "vendors",
  "items",
  "inventory_lots",
  "inventory_movements",
  "inventory_movement_batch",
  "inventory_positions",
  "inventory_statuses",
  "control_modules",
  "tgcm_events",
  "uom_types",
  "uom_units",
  "bom_revisions",
  "bom_components",
  "production_reviews",
  "work_orders",
  "work_order_components",
  "purchase_orders",
  "purchase_order_line_items",
  "sales_orders",
  "sales_order_line_items",
  "non_conformances",
  "non_con_lot_link",
  "customer_feedback",
  "technical_support_log",
  "tech_support_cm_link",
  "world_country",
  "world_state",
  "world_city",
  "users",
  "shipment_records",
  "shipment_packages",
  "locations",
  "returned_materials",
  "service_events",
  "issue_tracker",
] as const;

export type TABLENAME = (typeof TABLENAMES)[number];

// export type TABLENAME =
//   | "audit_trails"
//   | "addresses"
//   | "contacts"
//   | "customers"
//   | "employees"
//   | "person_base"
//   | "company_base"
//   | "vendors"
//   | "items"
//   | "inventory_lots"
//   | "inventory_movements"
//   | "inventory_movement_batch"
//   | "inventory_positions"
//   | "inventory_statuses"
//   | "control_modules"
//   | "tgcm_events"
//   | "uom_types"
//   | "uom_units"
//   | "bom_revisions"
//   | "bom_components"
//   | "production_reviews"
//   | "work_orders"
//   | "work_order_components"
//   | "purchase_orders"
//   | "purchase_order_line_items"
//   | "sales_orders"
//   | "sales_order_line_items"
//   | "non_conformances"
//   | "non_con_lot_link"
//   | "customer_feedback"
//   | "technical_support_log"
//   | "tech_support_cm_link"
//   | "world_country"
//   | "world_state"
//   | "world_city"
//   | "users"
//   | "shipment_records"
//   | "shipment_packages"
//   | "locations"
//   | "returned_materials"
//   | "service_events"
//   | "issue_tracker";

export interface TableDefinitionRegistry {}

// Utility Types
type ExtractFieldsObject<T> = T extends { fields: infer F } ? F : never;

type GetTableFieldNames<TTable extends keyof TableDefinitionRegistry> =
  keyof ExtractFieldsObject<TableDefinitionRegistry[TTable]> & string;

type GetFieldType<
  TTable extends keyof TableDefinitionRegistry,
  TField extends GetTableFieldNames<TTable>,
> = ExtractFieldsObject<TableDefinitionRegistry[TTable]>[TField] extends {
  type: infer T;
}
  ? T extends FieldType
    ? T
    : never
  : never;

export type ValidateForeignKey<
  TTable extends keyof TableDefinitionRegistry,
  TField extends GetTableFieldNames<TTable>,
> = ExtractFieldsObject<TableDefinitionRegistry[TTable]>[TField] extends {
  foreignKey: infer FK;
  targetField: infer TF;
}
  ? FK extends keyof TableDefinitionRegistry
    ? TF extends GetTableFieldNames<FK>
      ? true
      : false
    : false
  : true;

// Table Field Types
export type FieldTypeValueMap = {
  string: string;
  select: TypedSelectOption<string, Record<string, any>, unknown>;
  date: Date;
  number: number;
  boolean: boolean;
  object: TableObject<TableDataObjectType>;
  uuid: IdAsUUID;
};

export type FieldType = keyof FieldTypeValueMap;

export const FieldTypeOperatorMap: { [K in FieldType]: FilterMode[] } = {
  string: ["==", "contains"],
  select: ["=="],
  date: ["==", ">", "<", "between"],
  number: ["==", ">", "<", "between"],
  boolean: ["=="],
  object: ["contains"],
  uuid: ["==", "contains"],
};

// Table Filter Types

const ALL_FILTER_OPERATORS = [
  "==",
  ">",
  "<",
  "contains",
  "between",
  "~",
] as const;
export type FilterMode = (typeof ALL_FILTER_OPERATORS)[number];
export const FILTER_MODE_MAP = {
  "==": "Is",
  ">": "Greater Than/After",
  "<": "Less Than/Before",
  contains: "Contains",
  between: "Between",
  "~": "RegEx",
} as const;

// Table Data Objects

export interface PackageDimensions {
  height: string;
  width: string;
  length: string;
}

export const PACKAGE_WEIGHT_UNITS = [
  "pound",
  "ounce",
  "gram",
  "kilogram",
] as const;
export type PackageWeightUnit = (typeof PACKAGE_WEIGHT_UNITS)[number];

export interface PackageWeight {
  value: string;
  unit: PackageWeightUnit;
}

type TableDataObjectType =
  | "linked_record"
  | "address"
  | "orderaddress"
  | "contact"
  | "pkg_dims"
  | "pkg_weight";

interface TableDataObjectBase<TObjType extends TableDataObjectType> {}

interface PackageDimsTableDataObject extends TableDataObjectBase<"pkg_dims"> {
  height: number;
  width: number;
  length: number;
}

export const BlankPkgDimsObject: PackageDimsTableDataObject = {
  height: 0.0,
  width: 0.0,
  length: 0.0,
};

interface PackageWeightTableDataObject extends TableDataObjectBase<"pkg_weight"> {
  value: number;
  unit: PackageWeightUnit;
}

export const BlankPkgWeightObject: PackageWeightTableDataObject = {
  value: 0.0,
  unit: "pound",
};

interface LinkedRecordTableDataObject extends TableDataObjectBase<"linked_record"> {
  rec_id: IdAsUUID;
  rec_table: TABLENAME;
  rec_name: string;
  parent_id: IdAsUUID | null;
  parent_table: TABLENAME | null;
}

interface AddressTableDataObject extends TableDataObjectBase<"address"> {
  attn: string | null;
  addressee: string | null;
  line_1: string;
  line_2: string | null;
  line_3: string | null;
  city: string;
  state: string;
  country: string;
  zip: string;
}

export const BlankAddressObject: AddressTableDataObject = {
  attn: null,
  addressee: null,
  line_1: "",
  line_2: null,
  line_3: null,
  city: "",
  state: "",
  country: "",
  zip: "",
};

interface OrderAddressTableDataObject extends AddressTableDataObject {
  phone: string | null;
  email: string | null;
}

export const BlankOrderAddressObject: OrderAddressTableDataObject = {
  attn: null,
  addressee: null,
  line_1: "",
  line_2: null,
  line_3: null,
  city: "",
  state: "",
  country: "",
  zip: "",
  phone: null,
  email: null,
};

interface ContactTableDataObject extends TableDataObjectBase<"contact"> {
  name: string;
  email: string | null;
  phone: string | null;
}

export const BlankContactObject: ContactTableDataObject = {
  name: "",
  email: null,
  phone: null,
};

type TableObject<TObjType extends TableDataObjectType> =
  TObjType extends "linked_record"
    ? LinkedRecordTableDataObject
    : TObjType extends "address"
      ? AddressTableDataObject
      : TObjType extends "contact"
        ? ContactTableDataObject
        : TObjType extends "pkg_dims"
          ? PackageDimsTableDataObject
          : TObjType extends "pkg_weight"
            ? PackageWeightTableDataObject
            : OrderAddressTableDataObject;

export type { TableDataObjectType, TableObject };
